import { ref, computed, watch, onMounted, onBeforeUnmount } from '@vue/composition-api'
import { Editor } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import FontFamily from '@tiptap/extension-font-family'
import { Extension } from '@tiptap/core'
import CodeBlock from '@tiptap/extension-code-block'
import Code from '@tiptap/extension-code'
import Blockquote from '@tiptap/extension-blockquote'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import CustomTableHeader from '../extensions/CustomTableHeader.js'
import CustomTableCell from '../extensions/CustomTableCell.js'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Placeholder from '@tiptap/extension-placeholder'
import ResizableImage from '../extensions/ResizableImage.js'
import ResizableVideo from '../extensions/ResizableVideo.js'

// Custom Code extension with inline styles
const CustomCode = Code.extend({
  renderHTML({ HTMLAttributes }) {
    return ['code', {
      ...HTMLAttributes,
      style: 'background: #f1f5f9; padding: 0.2em 0.4em; border-radius: 3px; font-family: Monaco, Menlo, Ubuntu Mono, monospace; font-size: 0.9em; color: #e11d48;'
    }, 0]
  },
})

// Custom CodeBlock extension with inline styles
const CustomCodeBlock = CodeBlock.extend({
  renderHTML({ HTMLAttributes }) {
    return ['pre', {
      ...HTMLAttributes,
      style: 'background: #1e293b; color: #e2e8f0; padding: 1em; border-radius: 6px; overflow-x: auto; margin: 1.5em 0;'
    }, ['code', {
      style: 'background: transparent; padding: 0; color: inherit; font-family: Monaco, Menlo, Ubuntu Mono, monospace;'
    }, 0]]
  },
})

// Custom Blockquote extension with inline styles
const CustomBlockquote = Blockquote.extend({
  renderHTML({ HTMLAttributes }) {
    return ['blockquote', {
      ...HTMLAttributes,
      style: 'margin: 1.5em 0; padding: 0.8em 1.2em; border-left: 4px solid #3b82f6; background: #f8fafc; font-style: italic; color: #64748b;'
    }, 0]
  },
})

// Custom FontSize extension
const FontSize = Extension.create({
  name: 'fontSize',
  
  addOptions() {
    return {
      types: ['textStyle'],
    }
  },
  
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize || null,
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {}
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
        },
      },
    ]
  },
  
  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run()
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run()
      },
    }
  },
})

// Custom LineHeight extension
const LineHeight = Extension.create({
  name: 'lineHeight',
  
  addOptions() {
    return {
      types: ['paragraph', 'heading'],
    }
  },
  
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: element => element.style.lineHeight || null,
            renderHTML: attributes => {
              if (!attributes.lineHeight) {
                return {}
              }
              return {
                style: `line-height: ${attributes.lineHeight}`,
              }
            },
          },
        },
      },
    ]
  },
  
  addCommands() {
    return {
      setLineHeight: lineHeight => ({ commands }) => {
        return this.options.types.every(type => commands.updateAttributes(type, { lineHeight }))
      },
      unsetLineHeight: () => ({ commands }) => {
        return this.options.types.every(type => commands.resetAttributes(type, 'lineHeight'))
      },
    }
  },
})

export function useEditor(props, emit) {
  // Safety check for props
  if (!props) {
    console.error('❌ BBL Editor: Props is null or undefined')
    const editor = ref(null)
    const isEditorReady = ref(false)
    const editorError = ref('Props not provided')
    const missingDependencies = ref(['Props not provided'])
    return {
      editor,
      isEditorReady,
      currentContent: computed(() => ''),
      editorError,
      missingDependencies,
      executeCommand: () => {},
      isActive: () => false,
      getContent: () => '',
      setContent: () => {},
      focus: () => {},
      blur: () => {}
    }
  }

  console.log('🔧 BBL Editor: useEditor called with props:', props)

  const editor = ref(null)
  const isEditorReady = ref(false)
  const internalValue = ref(props.value || '')
  const editorError = ref(null)
  const missingDependencies = ref([])
  const initializationTimeout = ref(null)

  const currentContent = computed(() => {
    if (!editor.value || !props) return ''
    const outputFormat = props.outputFormat || 'html'
    return outputFormat === 'json' 
      ? JSON.stringify(editor.value.getJSON())
      : editor.value.getHTML()
  })

  const checkDependencies = () => {
    const missing = []
    
    try {
      console.log('🔍 BBL Editor: Starting dependency check...')
      
      // Check Vue Composition API first (most critical)
      if (typeof ref !== 'function') {
        missing.push('@vue/composition-api (ref)')
        console.error('❌ Missing: @vue/composition-api - ref function not available')
      } else {
        console.log('✅ Vue Composition API: ref available')
      }
      
      if (typeof computed !== 'function') {
        missing.push('@vue/composition-api (computed)')
        console.error('❌ Missing: @vue/composition-api - computed function not available')
      } else {
        console.log('✅ Vue Composition API: computed available')
      }
      
      if (typeof watch !== 'function') {
        missing.push('@vue/composition-api (watch)')
        console.error('❌ Missing: @vue/composition-api - watch function not available')
      } else {
        console.log('✅ Vue Composition API: watch available')
      }
      
      if (typeof onMounted !== 'function') {
        missing.push('@vue/composition-api (onMounted)')
        console.error('❌ Missing: @vue/composition-api - onMounted function not available')
      } else {
        console.log('✅ Vue Composition API: onMounted available')
      }

      // Check Tiptap dependencies by trying to use them
      try {
        if (!Editor || typeof Editor !== 'function') {
          missing.push('@tiptap/vue-2 (Editor)')
          console.error('❌ Missing: @tiptap/vue-2 - Editor class not available')
        } else {
          console.log('✅ Tiptap: Editor class available')
        }
      } catch (e) {
        missing.push('@tiptap/vue-2 (Editor)')
        console.error('❌ Error accessing Editor:', e.message)
      }

      try {
        if (!StarterKit || typeof StarterKit.configure !== 'function') {
          missing.push('@tiptap/starter-kit')
          console.error('❌ Missing: @tiptap/starter-kit')
        } else {
          console.log('✅ Tiptap: StarterKit available')
        }
      } catch (e) {
        missing.push('@tiptap/starter-kit')
        console.error('❌ Error accessing StarterKit:', e.message)
      }

      // Check other critical extensions
      const extensionChecks = [
        { name: 'TextAlign', module: TextAlign, package: '@tiptap/extension-text-align' },
        { name: 'Underline', module: Underline, package: '@tiptap/extension-underline' },
        { name: 'TextStyle', module: TextStyle, package: '@tiptap/extension-text-style' },
        { name: 'Color', module: Color, package: '@tiptap/extension-color' },
        { name: 'Highlight', module: Highlight, package: '@tiptap/extension-highlight' },
        { name: 'Link', module: Link, package: '@tiptap/extension-link' },
        { name: 'FontFamily', module: FontFamily, package: '@tiptap/extension-font-family' },
        { name: 'Extension', module: Extension, package: '@tiptap/core (Extension)' },
        { name: 'CodeBlock', module: CodeBlock, package: '@tiptap/extension-code-block' },
        { name: 'Code', module: Code, package: '@tiptap/extension-code' },
        { name: 'Blockquote', module: Blockquote, package: '@tiptap/extension-blockquote' },
        { name: 'Table', module: Table, package: '@tiptap/extension-table' },
        { name: 'TableRow', module: TableRow, package: '@tiptap/extension-table-row' },
        { name: 'TaskList', module: TaskList, package: '@tiptap/extension-task-list' },
        { name: 'TaskItem', module: TaskItem, package: '@tiptap/extension-task-item' },
        { name: 'Placeholder', module: Placeholder, package: '@tiptap/extension-placeholder' }
      ]

      extensionChecks.forEach(({ name, module, package: pkg }) => {
        try {
          if (!module) {
            missing.push(pkg)
            console.error(`❌ Missing: ${pkg} (${name})`)
          } else {
            console.log(`✅ Extension: ${name} available`)
          }
        } catch (e) {
          missing.push(pkg)
          console.error(`❌ Error accessing ${name}:`, e.message)
        }
      })
      
    } catch (error) {
      console.error('❌ Critical error during dependency check:', error)
      missing.push('Critical dependency check error: ' + error.message)
    }
    
    console.log(`🔍 BBL Editor: Dependency check complete. Missing: ${missing.length}`)
    if (missing.length > 0) {
      console.error('❌ Missing dependencies:', missing)
    } else {
      console.log('✅ All dependencies available!')
    }
    
    missingDependencies.value = missing
    return missing.length === 0
  }

  const initializeEditor = () => {
    try {
      console.log('🚀 BBL Editor: Starting editor initialization...')
      console.log('🔧 BBL Editor: Props received:', {
        toolbarConfig: props.toolbarConfig,
        extensionConfig: props.extensionConfig,
        placeholder: props.placeholder
      })
      
      // Check dependencies first
      if (!checkDependencies()) {
        console.error('❌ BBL Editor: Dependency check failed')
        editorError.value = 'Missing required dependencies'
        emit('error', {
          type: 'missing-dependencies',
          dependencies: missingDependencies.value,
          message: 'Please install all required dependencies'
        })
        return
      }

      console.log('✅ BBL Editor: All dependencies available, creating editor...')

      // Ensure props have default values to prevent null errors
      const safeToolbarConfig = props.toolbarConfig || {
        bold: true,
        italic: true,
        underline: true,
        strike: true,
        code: true,
        textColor: true,
        highlight: true,
        fontFamily: true,
        fontSize: true,
        textAlign: true,
        lineHeight: true,
        headings: true,
        lists: true,
        blockquote: true,
        codeBlock: true,
        horizontalRule: true,
        link: true,
        image: true,
        video: true,
        table: true,
        clearFormatting: true,
        sourceCode: true,
        undo: true,
        redo: true
      }

      const safeExtensionConfig = props.extensionConfig || {
        image: {
          allowResize: true,
          allowAlignment: true,
          allowDelete: true,
          maxWidth: '100%',
          quality: 0.8
        },
        video: {
          allowResize: true,
          allowAlignment: true,
          allowDelete: true,
          maxWidth: '100%',
          autoplay: false
        },
        table: {
          resizable: true,
          allowRowControls: true,
          allowColumnControls: true,
          cellSelection: true
        },
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true
        },
        textAlign: {
          types: ['heading', 'paragraph']
        }
      }

      console.log('🔧 BBL Editor: Using safe configs:', {
        toolbarConfig: safeToolbarConfig,
        extensionConfig: safeExtensionConfig
      })

      // Build extensions array based on configuration
      const extensions = [
        StarterKit.configure({
          image: false,
          youtube: false,
          table: false,
          codeBlock: false,
          code: false,
          blockquote: false, // Disable default blockquote
        }),
        CustomCode,
        CustomCodeBlock,
        CustomBlockquote, // Add our custom blockquote
        TextStyle,
        Placeholder.configure({
          placeholder: props.placeholder || 'Start writing...',
        }),
      ]

      console.log('🔧 BBL Editor: Base extensions configured')

      // Conditionally add extensions based on configuration
      if (safeExtensionConfig.textAlign) {
        extensions.push(TextAlign.configure(safeExtensionConfig.textAlign))
        console.log('✅ Added: TextAlign extension')
      }

      if (safeToolbarConfig.underline) {
        extensions.push(Underline)
        console.log('✅ Added: Underline extension')
      }

      if (safeToolbarConfig.textColor) {
        extensions.push(Color.configure({ types: [TextStyle.name] }))
        console.log('✅ Added: Color extension')
      }

      if (safeToolbarConfig.highlight) {
        extensions.push(Highlight.configure({ multicolor: true }))
        console.log('✅ Added: Highlight extension')
      }

      if (safeToolbarConfig.fontFamily) {
        extensions.push(FontFamily.configure({
          types: [TextStyle.name],
        }))
        console.log('✅ Added: FontFamily extension')
      }

      if (safeToolbarConfig.fontSize) {
        extensions.push(FontSize)
        console.log('✅ Added: FontSize extension')
      }

      if (safeToolbarConfig.lineHeight) {
        extensions.push(LineHeight)
        console.log('✅ Added: LineHeight extension')
      }

      if (safeToolbarConfig.link) {
        extensions.push(Link.configure({
          ...safeExtensionConfig.link,
          HTMLAttributes: {
            class: 'editor-link',
          },
        }))
        console.log('✅ Added: Link extension')
      }

      if (safeToolbarConfig.image) {
        extensions.push(ResizableImage.configure(safeExtensionConfig.image))
        console.log('✅ Added: ResizableImage extension')
      }

      if (safeToolbarConfig.video) {
        extensions.push(ResizableVideo.configure(safeExtensionConfig.video))
        console.log('✅ Added: ResizableVideo extension')
      }

      if (safeToolbarConfig.table) {
        extensions.push(
          Table.configure(safeExtensionConfig.table),
          TableRow,
          CustomTableHeader,
          CustomTableCell
        )
        console.log('✅ Added: Table extensions')
      }

      if (safeToolbarConfig.lists) {
        extensions.push(
          TaskList,
          TaskItem.configure({
            nested: true,
          })
        )
        console.log('✅ Added: List extensions')
      }

      console.log(`🔧 BBL Editor: Total extensions: ${extensions.length}`)
      console.log('🚀 BBL Editor: Creating Tiptap Editor instance...')

      editor.value = new Editor({
        content: props.value || '',
        editable: props.editable !== false,
        autofocus: props.autofocus || false,
        extensions,
        onUpdate: () => {
          const newContent = currentContent.value
          if (newContent !== internalValue.value) {
            internalValue.value = newContent
            emit('input', newContent)
            emit('update', newContent)
          }
          // Re-enhance tables after updates with a delay
          setTimeout(() => {
            emit('enhance-tables')
          }, 200)
        },
        onCreate: () => {
          console.log('🎉 BBL Editor: Editor created successfully!')
          
          // Clear the initialization timeout
          if (initializationTimeout.value) {
            clearTimeout(initializationTimeout.value)
            initializationTimeout.value = null
          }
          
          isEditorReady.value = true
          editorError.value = null
          emit('ready', editor.value)
          emit('created', editor.value)
          // Add table enhancements
          setTimeout(() => {
            emit('enhance-tables')
          }, 100)
        },
        onFocus: () => {
          emit('focus')
        },
        onBlur: () => {
          emit('blur')
        },
        onDestroy: () => {
          console.log('🗑️ BBL Editor: Editor destroyed')
          emit('destroyed')
        },
      })
      
      console.log('✅ BBL Editor: Editor instance created, waiting for onCreate...')
      
    } catch (error) {
      console.error('❌ BBL Editor: Initialization failed:', error)
      console.error('❌ Error stack:', error.stack)
      editorError.value = error.message
      emit('error', {
        type: 'initialization-failed',
        error: error.message,
        stack: error.stack,
        message: 'Failed to initialize editor'
      })
    }
  }

  const executeCommand = (command, options = {}) => {
    if (!editor.value) return
    
    // Handle special commands
    switch (command) {
      case 'setTextAlign':
        editor.value.chain().focus().setTextAlign(options).run()
        break
      case 'setColor':
        editor.value.chain().focus().setColor(options).run()
        break
      case 'setHighlight':
        editor.value.chain().focus().setHighlight(options).run()
        break
      case 'setFontFamily':
        editor.value.chain().focus().setFontFamily(options).run()
        break
      case 'setFontSize':
        editor.value.chain().focus().setFontSize(options).run()
        break
      case 'setLineHeight':
        editor.value.chain().focus().setLineHeight(options).run()
        break
      case 'setLink':
        editor.value.chain().focus().setLink(options).run()
        break
      case 'setResizableImage':
        editor.value.chain().focus().setResizableImage(options).run()
        break
      case 'setResizableVideo':
        editor.value.chain().focus().setResizableVideo(options).run()
        break
      case 'insertTable':
        editor.value.chain().focus().insertTable(options).run()
        break
      case 'toggleHeading':
        editor.value.chain().focus().toggleHeading(options).run()
        break
      case 'setParagraph':
        editor.value.chain().focus().setParagraph().run()
        break
      case 'toggleBlockquote':
        editor.value.chain().focus().toggleBlockquote().run()
        break
      case 'setBlockquote':
        editor.value.chain().focus().setBlockquote().run()
        break
      case 'unsetBlockquote':
        editor.value.chain().focus().unsetBlockquote().run()
        break
      case 'unsetAllMarks':
        editor.value.chain().focus().unsetAllMarks().run()
        break
      case 'clearNodes':
        editor.value.chain().focus().clearNodes().run()
        break
      default:
        // Handle standard commands
        if (typeof editor.value.chain().focus()[command] === 'function') {
          editor.value.chain().focus()[command](options).run()
        }
    }
  }
  
  const isActive = (name, attributes = {}) => {
    if (!editor.value) return false
    return editor.value.isActive(name, attributes)
  }

  const getContent = (format = null) => {
    if (!editor.value) return ''
    const outputFormat = format || props.outputFormat
    return outputFormat === 'json' 
      ? editor.value.getJSON()
      : editor.value.getHTML()
  }
  
  const setContent = (content, emitUpdate = true) => {
    if (!editor.value) return
    editor.value.commands.setContent(content, emitUpdate)
  }
  
  const focus = () => {
    if (editor.value) {
      editor.value.commands.focus()
    }
  }
  
  const blur = () => {
    if (editor.value) {
      editor.value.commands.blur()
    }
  }

  // Watch for prop changes
  watch(() => props?.value, (newValue) => {
    if (editor.value && newValue !== internalValue.value) {
      editor.value.commands.setContent(newValue, false)
      internalValue.value = newValue
    }
  })

  watch(() => props?.editable, (newValue) => {
    if (editor.value) {
      editor.value.setEditable(newValue)
    }
  })

  onMounted(() => {
    console.log('🔄 BBL Editor: Component mounted, starting initialization...')
    
    // Set a timeout to detect stuck initialization
    initializationTimeout.value = setTimeout(() => {
      if (!isEditorReady.value && !editorError.value) {
        console.error('⏰ BBL Editor: Initialization timeout - editor stuck in loading state')
        editorError.value = 'Editor initialization timeout - check console for details'
        emit('error', {
          type: 'initialization-timeout',
          message: 'Editor failed to initialize within 10 seconds',
          dependencies: missingDependencies.value
        })
      }
    }, 10000) // 10 second timeout
    
    initializeEditor()
  })

  onBeforeUnmount(() => {
    console.log('🧹 BBL Editor: Component unmounting...')
    
    if (initializationTimeout.value) {
      clearTimeout(initializationTimeout.value)
    }
    
    if (editor.value) {
      editor.value.destroy()
    }
  })

  return {
    editor,
    isEditorReady,
    currentContent,
    editorError,
    missingDependencies,
    executeCommand,
    isActive,
    getContent,
    setContent,
    focus,
    blur
  }
}